'use client'

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { OperatingCost, OperatingCostListItem } from '@garage/shared'
import { OPERATING_COST_TYPE_LABELS } from '@garage/shared'
import {
  Button,
  IconButton,
  DataTable,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  type Column,
  EditIcon,
  DeleteIcon,
  AddIcon,
  ChevronRightIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { OperatingCostFormDialog } from '@/components/OperatingCostFormDialog.js'

/** First and last day ("YYYY-MM-DD") of a given month, 0-indexed like Date. */
function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return {
    from: `${year}-${String(month + 1).padStart(2, '0')}-01`,
    to: `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  }
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const

export function OperatingCostsTab() {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t, i18n } = useTranslation()

  const today = new Date()
  const [listYear, setListYear] = useState(today.getFullYear())
  const [listMonth, setListMonth] = useState(today.getMonth())

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [editingCost, setEditingCost] = useState<OperatingCost | undefined>()
  const [formDialogOpen, setFormDialogOpen] = useState(false)

  const monthName = (month: number) => t(`calendar.months.${MONTH_KEYS[month]}`)
  const listRange = monthRange(listYear, listMonth)

  // Monthly costs
  const { data: monthlyData, isLoading: isMonthlyLoading } = useQuery({
    queryKey: qk.operatingCosts({ ...listRange, pageSize: 200 }),
    queryFn: () =>
      api.operatingCosts.list({
        from: listRange.from,
        to: listRange.to,
        pageSize: 200,
      }),
  })

  // Recurring costs (fetch with a wide range)
  const recurringRange = {
    from: '2000-01-01',
    to: new Date(listYear + 20, 11, 31).toISOString().slice(0, 10),
  }
  const { data: recurringData, isLoading: isRecurringLoading } = useQuery({
    queryKey: ['operating-costs-recurring', { ...recurringRange }],
    queryFn: () =>
      api.operatingCosts.list({
        from: recurringRange.from,
        to: recurringRange.to,
        pageSize: 200,
      }),
  })

  const goToMonth = (delta: number) => {
    const next = new Date(listYear, listMonth + delta, 1)
    setListYear(next.getFullYear())
    setListMonth(next.getMonth())
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.operatingCosts.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.operatingCosts({}) })
      queryClient.invalidateQueries({ queryKey: ['finance-advanced'] })
      snackbar.success(t('accounting.advanced.costRemoved'))
      setDeleteId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('common.errorOccurred'))
    },
  })

  const handleEdit = (cost: OperatingCostListItem) => {
    setEditingCost(cost)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingCost(undefined)
  }

  const handleAddNew = () => {
    setEditingCost(undefined)
    setFormDialogOpen(true)
  }

  // Filter recurring costs for current list
  const recurringCosts = (recurringData?.items ?? []).filter(
    (c) => c.recurringMonthly && c.date <= listRange.to
  )

  // Format recurring end date
  const formatRecurringLabel = (cost: OperatingCost): string => {
    if (!cost.recurringMonthly) return ''
    if (!cost.endsOn) return t('accounting.advanced.recurringMonthly')
    const endDate = new Date(cost.endsOn)
    const locale = i18n.language === 'ar' ? 'ar-u-nu-latn' : 'en-US'
    const monthName = endDate.toLocaleDateString(locale, { month: 'short' })
    return t('accounting.advanced.recurringUntil', { month: monthName, year: endDate.getFullYear() })
  }

  const monthlyCosts = monthlyData?.items ?? []
  const isEmpty = monthlyCosts.length === 0

  const columns: Column<OperatingCostListItem>[] = [
    {
      key: 'date',
      header: t('calendar.date'),
      cell: (row) => <div>{formatters.date(row.date)}</div>,
    },
    {
      key: 'type',
      header: t('accounting.advanced.type'),
      cell: (row) => {
        const label = t(`accounting.advanced.costTypes.${row.type}`, OPERATING_COST_TYPE_LABELS[row.type])
        return <div>{label}</div>
      },
    },
    {
      key: 'description',
      header: t('accounting.advanced.description'),
      cell: (row) => <div>{row.description}</div>,
    },
    {
      key: 'amountBeforeVatMinor',
      header: t('accounting.beforeVat'),
      cell: (row) => <div className="tabular">{formatters.amount(row.amountBeforeVatMinor)}</div>,
      numeric: true,
    },
    {
      key: 'vatMinor',
      header: t('accounting.vat'),
      cell: (row) => <div className="tabular">{formatters.amount(row.vatMinor)}</div>,
      numeric: true,
    },
    {
      key: 'total',
      header: t('dashboard.columns.total'),
      cell: (row) => (
        <div className="tabular font-medium">
          {formatters.amount(row.amountBeforeVatMinor + row.vatMinor)}
        </div>
      ),
      numeric: true,
    },
    {
      key: 'recurringMonthly',
      header: t('accounting.advanced.recurring'),
      cell: (row) => (
        <div className="text-body-sm text-on-surface-variant">
          {row.recurringMonthly ? formatRecurringLabel(row) : '—'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          <IconButton
            icon={<EditIcon className="w-5 h-5" />}
            label={t('common.edit')}
            variant="standard"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-5 h-5" />}
            label={t('common.delete')}
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteId(row.id)
            }}
          />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={handleAddNew}>
          {t('accounting.advanced.addCost')}
        </Button>
      </div>

      {/* Recurring costs section */}
      {recurringCosts.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('accounting.advanced.recurringCosts')}</h2>
          <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
            <DataTable
              columns={columns}
              rows={recurringCosts}
              rowKey={(c) => c.id}
              stackOnMobile
            />
          </div>
        </section>
      )}

      {/* Monthly costs section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconButton
              icon={<ChevronRightIcon className="w-5 h-5 rotate-180 rtl:rotate-0" />}
              label={t('calendar.previousMonth')}
              variant="standard"
              onClick={() => goToMonth(-1)}
            />
            <h2 className="text-title-md text-on-surface w-40 text-center">
              {monthName(listMonth)} {listYear}
            </h2>
            <IconButton
              icon={<ChevronRightIcon className="w-5 h-5 rtl:rotate-180" />}
              label={t('calendar.nextMonth')}
              variant="standard"
              onClick={() => goToMonth(1)}
            />
          </div>
          <Button
            variant="text"
            onClick={() => { setListYear(today.getFullYear()); setListMonth(today.getMonth()) }}
          >
            {t('calendar.today')}
          </Button>
        </div>

        {isMonthlyLoading || isRecurringLoading ? (
          <Skeleton lines={3} />
        ) : isEmpty ? (
          <EmptyState
            title={t('accounting.advanced.noCosts', { month: monthName(listMonth), year: listYear })}
            description={t('accounting.advanced.noCostsDesc')}
            action={<Button variant="tonal" onClick={handleAddNew}>{t('accounting.advanced.addCost')}</Button>}
          />
        ) : (
          <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
            <DataTable
              columns={columns}
              rows={monthlyCosts}
              rowKey={(c) => c.id}
              stackOnMobile
            />
          </div>
        )}
      </section>

      <OperatingCostFormDialog open={formDialogOpen} cost={editingCost} onClose={handleFormClose} />

      <ConfirmDialog
        open={!!deleteId}
        title={t('accounting.advanced.removeCostTitle')}
        message={t('accounting.advanced.removeCostMessage')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId)
        }}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
